import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { JSX, useEffect, useState } from "react";

interface DualRangeSliderProps {
  /**
   *  Used for the QA automated test handle, and as the element's unique id
   */
  testId: string;
  /**
   *  The label to show above the range
   */
  label: string | JSX.Element;
  /**
   *  The min value in the range
   */
  minValue: number;
  /**
   *  The max value in the range
   */
  maxValue: number;
  /**
   *  A method to set the currently selected range
   */
  onChange: (value: [number, number]) => void;
  /**
   *  Determines the size of the jumps between possible values. If not passed, it is defaulted to 1, which means that only integer numbers would be used.
   */
  step?: number;
  /**
   *  An array of numbers to hold the currently selected range
   */
  value: [number, number];
  /** 
   * When true, the upper textbox renders "{maxValue - 1}+" at maxValue. The wire value stays maxValue. 
  */
  maxAsPlus?: boolean;
}

/**
 * A custom component to display an input type "range" with a bidirectional communication with two inputs that also allow to provide the range values.
 */
const DualRangeSlider: React.FC<DualRangeSliderProps> = (props: DualRangeSliderProps) => {

  const [lowerBoundsValue, setLowerBoundValue] = useState(props.value[0]);
  const [upperBoundsValue, setUpperBoundValue] = useState(props.value[1]);

  const [isMouseDown, setIsMouseDown] = useState(false);

  const lowerBoundCeiling = props.maxAsPlus
    ? Math.min(upperBoundsValue, props.maxValue - 1)
    : upperBoundsValue;

  const validateAndSetLowerBound = (newLowerBoundsValue: number) => {
    let updateBoundsValue = newLowerBoundsValue;
    // Validate we don't go under the min value
    if (newLowerBoundsValue < props.minValue)
      updateBoundsValue = props.minValue;
    // Validate we don't go over the upper bound (and stay below the maxValue slot reserved for "+" when maxAsPlus)
    else if (newLowerBoundsValue > lowerBoundCeiling)
      updateBoundsValue = lowerBoundCeiling;
    setLowerBoundValue(updateBoundsValue);
  }

  const validateAndSetUpperBound = (newUpperBoundsValue: number) => {
    let updateBoundsValue = newUpperBoundsValue;
    // Validate we don't go under the lowerBoundsValue
    if (newUpperBoundsValue < lowerBoundsValue)
      updateBoundsValue = lowerBoundsValue;
    // Validate we don't go over the maxValue
    else if (newUpperBoundsValue > props.maxValue)
      updateBoundsValue = props.maxValue
    setUpperBoundValue(updateBoundsValue);
  }

  useEffect(() => {
    props.onChange([lowerBoundsValue, upperBoundsValue]);
  }, [lowerBoundsValue, upperBoundsValue]);

  useEffect(() => {
    if (!isMouseDown) {
      if (lowerBoundsValue !== props.value[0]) {
        setLowerBoundValue(props.value[0]);
      }

      if (upperBoundsValue !== props.value[1]) {
        setUpperBoundValue(props.value[1]);
      }
    }
  }, [props.value]);

  return (
    <div className="dual-range-slider-main-container">
      <label className="slider-label">{props.label}</label>
      <div className="slider-range-container">
        <QESTextInput
          type="range"
          className={`input-range-container${lowerBoundsValue === props.maxValue && upperBoundsValue === props.maxValue ? " input-range-on-top" : ""}`}
          min={props.minValue}
          max={props.maxValue}
          step={props.step}
          value={lowerBoundsValue}
          onChange={(e) => validateAndSetLowerBound(Number(e.target.value))}
          onPointerDown={(e) => {
            (e.currentTarget as HTMLInputElement).setPointerCapture(e.pointerId);
            setIsMouseDown(true);
          }}
          onPointerUp={() => setIsMouseDown(false)}
          testId={`${props.testId}-min-value-left-slider`}
          hideLabel={true}
          label=""
        />

        <QESTextInput
          type="range"
          className="input-range-container"
          min={props.minValue}
          max={props.maxValue}
          step={props.step}
          value={upperBoundsValue}
          onChange={(e) => validateAndSetUpperBound(Number(e.target.value))}
          onPointerDown={(e) => {
            (e.currentTarget as HTMLInputElement).setPointerCapture(e.pointerId);
            setIsMouseDown(true);
          }}
          onPointerUp={() => setIsMouseDown(false)}
          testId={`${props.testId}-min-value-right-slider`}
          hideLabel={true}
          label=""
        />

        <div className="slider">
          <div
            className="slider__range"
            style={{
              left: `${((lowerBoundsValue - props.minValue) / (props.maxValue - props.minValue)) * 100}%`,
              right: `${100 - ((upperBoundsValue - props.minValue) / (props.maxValue - props.minValue)) * 100}%`,
            }}
          />
        </div>
      </div>

      <div className="slider-input-container">
        <QESTextInput
          type="number"
          value={lowerBoundsValue}
          min={props.minValue}
          max={lowerBoundCeiling}
          onChange={(e) => validateAndSetLowerBound(Number(e.target.value))}
          testId={`${props.testId}-min-value-input`}
          hideLabel={true}
          label=""
        />

        <span>-</span>

        {props.maxAsPlus ? (
          <QESTextInput
            type="number"
            containerClassName="dual-range-slider__max-input"
            value={upperBoundsValue === props.maxValue ? props.maxValue - 1 : upperBoundsValue}
            min={upperBoundsValue === props.maxValue ? lowerBoundsValue - 1 : lowerBoundsValue}
            max={props.maxValue}
            aria-valuetext={upperBoundsValue === props.maxValue ? `${props.maxValue - 1} plus` : undefined}
            onChange={(e) => {
              const raw = Number(e.target.value);
              // Decrementing from the "+" slot (actual maxValue, displayed maxValue - 1) should land on maxValue - 1, not maxValue - 2
              if (upperBoundsValue === props.maxValue && raw === props.maxValue - 2) {
                validateAndSetUpperBound(props.maxValue - 1);
                return;
              }
              validateAndSetUpperBound(raw);
            }}
            inputAppend={upperBoundsValue === props.maxValue ? <span aria-hidden="true">+</span> : undefined}
            testId={`${props.testId}-max-value-input`}
            hideLabel={true}
            label=""
          />
        ) : (
          <QESTextInput
            type="number"
            value={upperBoundsValue}
            min={lowerBoundsValue}
            max={props.maxValue}
            onChange={(e) => validateAndSetUpperBound(Number(e.target.value))}
            testId={`${props.testId}-max-value-input`}
            hideLabel={true}
            label=""
          />
        )}
      </div>
    </div>
  );
};

export default DualRangeSlider